import { type Island, IslandType, type Kitchen } from '@rta/rta-blueprint-library';

import { getCountertopTopElevation } from './get-countertop-top-elevation';

export type IsJointCountertopParams = {
	island: Island;
	kitchen: Kitchen;
};

const ANGLE_MATCH_TOLERANCE = 1;
const DEPTH_MATCH_TOLERANCE = 0.5;
const HEIGHT_MATCH_TOLERANCE = 0.5;
const CENTER_MATCH_TOLERANCE = 0.5;
const BUTTED_PADDING_MAX = 1;

const normalizeAngle = (angle: number): number => ((angle % 360) + 360) % 360;

const angleDelta = (a: number, b: number): number => {
	const delta = Math.abs(normalizeAngle(a) - normalizeAngle(b));
	return Math.min(delta, 360 - delta);
};

// The cross-run center: islands facing along Z (axis.y 0/180) run left-to-right and share a
// Z center line; islands facing along X (axis.y 90/270) run front-to-back and share an X center
// line. Any non-cardinal facing (e.g. a Corner's 45° panels) is not a clean inline run — null.
const getRunCrossCenter = (island: Island): number | null => {
	const { axis, origin } = island;
	const angle = normalizeAngle(axis.y);
	if (angleDelta(angle, 0) <= ANGLE_MATCH_TOLERANCE || angleDelta(angle, 180) <= ANGLE_MATCH_TOLERANCE) return origin.z;
	if (angleDelta(angle, 90) <= ANGLE_MATCH_TOLERANCE || angleDelta(angle, 270) <= ANGLE_MATCH_TOLERANCE) return origin.x;
	return null;
};

// Butted side-to-side: a direct connection between the two islands whose padding is (near) zero
// AND whose connecting panels are BOTH side panels — the countertops physically touch edge to edge.
// A front/back connection, or any gap, fails this and is not a joint.
const isButtedSideConnected = (a: Island, b: Island): boolean => {
	const connection = a.connections.getSourceConnections().find((c) => c.targetIsland?.id === b.id) ??
		b.connections.getSourceConnections().find((c) => c.targetIsland?.id === a.id) ??
		null;
	if (!connection) return false;
	if (connection.padding > BUTTED_PADDING_MAX) return false;
	const { sourcePanel, targetPanel } = connection;
	return Boolean(sourcePanel?.isSidePanel && targetPanel?.isSidePanel);
};

// A joint pair matches on true COUNTERTOP geometry — top elevation and depth including overhang,
// never the island's own size — plus in-line facing and a collinear cross-run center. Physical
// adjacency (butted, side-to-side) is checked separately in isButtedSideConnected, since that only
// has to hold for the specific pair linking two islands together — not for every pair in a group.
const matchesJointProfile = (a: Island, b: Island): boolean => {
	if (a.id === b.id) return false;
	if (a.type === IslandType.Corner || b.type === IslandType.Corner) return false;
	if (Math.abs(getCountertopTopElevation(a) - getCountertopTopElevation(b)) > HEIGHT_MATCH_TOLERANCE) return false;
	if (angleDelta(a.axis.y, b.axis.y) > ANGLE_MATCH_TOLERANCE) return false;
	if (Math.abs(a.overallSize.depth - b.overallSize.depth) > DEPTH_MATCH_TOLERANCE) return false;

	const aCenter = getRunCrossCenter(a);
	const bCenter = getRunCrossCenter(b);
	if (aCenter === null || bCenter === null) return false;
	return Math.abs(aCenter - bCenter) <= CENTER_MATCH_TOLERANCE;
};

const formsJointPair = (a: Island, b: Island): boolean => matchesJointProfile(a, b) && isButtedSideConnected(a, b);

/**
 * Groups islands whose countertops merge into continuous joint slabs. Each group is a connected
 * cluster (every member butted side-to-side to at least one other member already in the group)
 * where EVERY pair in the group — not just the adjacent pair that linked them — is in line,
 * collinear, the same height, and the same depth. Checking adjacent pairs alone would let
 * tolerance drift chain together (A matches B, B matches C, but A and C don't) into one group;
 * requiring every candidate to match the WHOLE group so far catches that. Corner (L) islands
 * never join, and any countertop that differs is left in its own group (dropped here, since only
 * clusters of 2+ form a joint).
 */
export const getJointCountertopGroups = (kitchen: Kitchen): Array<Array<Island>> => {
	const islands = kitchen.design.islands.children.filter((island) => island.type !== IslandType.Corner);
	const visited = new Set<string>();
	const groups: Array<Array<Island>> = [];

	islands.forEach((island) => {
		if (visited.has(island.id)) return;

		const group: Array<Island> = [];
		const stack: Array<Island> = [island];
		visited.add(island.id);

		while (stack.length > 0) {
			const current = stack.pop() as Island;
			group.push(current);
			islands.forEach((candidate) => {
				if (visited.has(candidate.id)) return;
				if (!formsJointPair(current, candidate)) return;
				if (!group.every((member) => matchesJointProfile(candidate, member))) return;
				visited.add(candidate.id);
				stack.push(candidate);
			});
		}

		if (group.length >= 2) groups.push(group);
	});

	return groups;
};

export const isJointCountertop = (params: IsJointCountertopParams): boolean => {
	const { island, kitchen } = params;
	if (island.type === IslandType.Corner) return false;
	return getJointCountertopGroups(kitchen).some((group) => group.some((member) => member.id === island.id));
};
